<template>
  <div class="rules">
    <div v-if="!rules.length" class="rempty">No sub rules — all traffic passes</div>
    <div
      v-for="(r, i) in rules"
      :key="'r' + i + r.sub + r.mode"
      class="rrow"
      :class="{ open: r._open }"
    >
      <div class="rhead">
        <span class="rcar" @click="toggle(r)">▶</span>
        <select class="rsub" :value="r.sub" @change="setSub(r, i, $event.target.value)">
          <option v-for="s in subs" :key="s">{{ s }}</option>
        </select>
        <div class="rmode">
          <button type="button" class="bl" :class="{ on: r.mode !== 'whitelist' }" @click="setMode(r, i, 'blacklist')">BL</button>
          <button type="button" class="wl" :class="{ on: r.mode === 'whitelist' }" @click="setMode(r, i, 'whitelist')">WL</button>
        </div>
        <span class="rcount" :class="{ has: ruleCount(r) }" @click="toggle(r)">{{ countLabel(r) }}</span>
        <span class="rprev" @click="toggle(r)">{{ lenLabel(r.values) }}</span>
        <button type="button" class="raddx" @click="addExtra(r)">+ Another</button>
        <button type="button" class="rrm" @click="remove(i)">Remove</button>
      </div>
      <div class="rbody">
        <textarea
          class="rta"
          :value="r.values"
          @input="onVals(r, $event.target.value)"
          @blur="format(r)"
        ></textarea>
        <div v-for="(ex, ei) in extrasOf(r)" :key="'ex' + i + '-' + ei" class="rextra">
          <div class="rextra-h">
            <span class="rcount" :class="{ has: count(ex) }">{{ count(ex) }} extra</span>
            <span class="rprev">{{ lenLabel(ex) }}</span>
            <button type="button" class="rrm" @click="removeExtra(r, ei)">Remove extra</button>
          </div>
          <textarea
            class="rta"
            :value="ex"
            @input="onExtra(r, ei, $event.target.value)"
            @blur="formatExtra(r, ei)"
          ></textarea>
        </div>
      </div>
    </div>
    <div class="raddbar">
      <select v-model="addSub">
        <option value="">Sub…</option>
        <option v-for="s in subs" :key="'add'+s">{{ s }}</option>
      </select>
      <label class="ckl"><input type="checkbox" class="cb" v-model="addWl"> Whitelist</label>
      <button type="button" class="btn btn-s primary" @click="add">+ Add rule</button>
      <span class="hint">click a saved rule to edit its sub, mode or values</span>
    </div>
  </div>
</template>

<script>
import { SUB_OPTIONS, SUB_VALUES_LIMIT, normStr, valCount, ruleValCount } from '../../v2/status'

export default {
  name: 'SubRulesEditor',
  props: {
    rules: { type: Array, default: () => [] }
  },
  data() {
    return {
      subs: SUB_OPTIONS,
      addSub: '',
      addWl: false
    }
  },
  methods: {
    count: valCount,
    extrasOf(r) {
      return r.extras || []
    },
    ensureExtras(r) {
      if (!Array.isArray(r.extras)) this.$set(r, 'extras', [])
      return r.extras
    },
    ruleCount: ruleValCount,
    countLabel(r) {
      const n = ruleValCount(r)
      return n + ' value' + (n === 1 ? '' : 's')
    },
    fmtNum(n) {
      return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
    lenLabel(value) {
      const n = String(value || '').length
      return this.fmtNum(n) + ' / ' + this.fmtNum(SUB_VALUES_LIMIT)
    },
    toggle(r) {
      this.$set(r, '_open', !r._open)
    },
    emit() {
      this.$emit('change')
    },
    hasPair(sub, mode, skip) {
      return this.rules.some((r, i) => i !== skip && r.sub === sub && r.mode === mode)
    },
    hasSub(sub, skip) {
      return this.rules.some((r, i) => i !== skip && r.sub === sub)
    },
    setSub(r, i, sub) {
      if (this.hasSub(sub, i)) {
        this.notifyError('A rule for ' + sub + ' already exists — edit it')
        return
      }
      r.sub = sub
      this.emit()
    },
    setMode(r, i, mode) {
      if (this.hasPair(r.sub, mode, i) || this.hasSub(r.sub, i)) {
        this.notifyError('A rule for ' + r.sub + ' already exists — edit it')
        return
      }
      r.mode = mode
      this.emit()
    },
    cap(value) {
      if (value.length > SUB_VALUES_LIMIT) return value.slice(0, SUB_VALUES_LIMIT)
      return value
    },
    onVals(r, value) {
      r.values = this.cap(value)
      this.emit()
    },
    onExtra(r, ei, value) {
      this.ensureExtras(r)
      this.$set(r.extras, ei, this.cap(value))
      this.emit()
    },
    format(r, toast) {
      const before = r.values || ''
      const after = normStr(before)
      if (before === after) {
        if (toast) this.notifySuccess(valCount(after) + ' values — trimmed, comma separated')
        return
      }
      r.values = after
      this.emit()
      this.notifySuccess(valCount(after) + ' values — trimmed, comma separated')
    },
    formatExtra(r, ei) {
      this.ensureExtras(r)
      const before = r.extras[ei] || ''
      const after = normStr(before)
      if (before === after) return
      this.$set(r.extras, ei, after)
      this.emit()
      this.notifySuccess(valCount(after) + ' values — trimmed, comma separated')
    },
    addExtra(r) {
      this.ensureExtras(r)
      r.extras.push('')
      this.$set(r, '_open', true)
      this.emit()
    },
    removeExtra(r, ei) {
      this.ensureExtras(r)
      r.extras.splice(ei, 1)
      this.emit()
    },
    remove(i) {
      const r = this.rules[i]
      const n = ruleValCount(r)
      if (!window.confirm('Remove ' + r.sub + ' ' + (r.mode === 'whitelist' ? 'whitelist' : 'blacklist') + ' rule (' + n + ' values)?')) return
      this.rules.splice(i, 1)
      this.emit()
    },
    add() {
      if (!this.addSub) {
        this.notifyError('Pick a sub first')
        return
      }
      const mode = this.addWl ? 'whitelist' : 'blacklist'
      if (this.hasSub(this.addSub) || this.hasPair(this.addSub, mode)) {
        this.notifyError('A rule for ' + this.addSub + ' already exists — edit it')
        return
      }
      this.rules.push({ sub: this.addSub, mode, values: '', extras: [], _open: true })
      this.addSub = ''
      this.addWl = false
      this.emit()
    }
  }
}
</script>
